import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { listCentralFillPharmaciesApi } from 'api/centralFill';
import { updatePartnerCatalogApi } from 'api/partner';
import CatalogDialogHeader from 'modules/CatalogManagement/components/CatalogDialogHeader';
import { formatForm, formatPackSize } from 'modules/CatalogManagement/utils/format';
import { handleError } from 'store/thunks/errorHandlerThunks';

// Mirrors e-scribe-ui's EditPartnerFormulary: the only thing actually
// editable for an existing partner catalog association is which compounding
// pharmacy fills it. Everything else shown here is read-only context, same
// as Catalog Management's own Edit dialog.
const EditPartnerCatalogItemDialog = ({ open, item, partnerUuid, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [compoundingPharmacyId, setCompoundingPharmacyId] = useState('');
  const [pharmacies, setPharmacies] = useState([]);
  const [loadingPharmacies, setLoadingPharmacies] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;

    setCompoundingPharmacyId(item.compoundingPharmacyId ?? '');
    setLoadingPharmacies(true);

    listCentralFillPharmaciesApi()
      .then((results) =>
        setPharmacies(
          [...(results ?? [])].sort((left, right) =>
            String(left?.name ?? '').localeCompare(String(right?.name ?? ''), undefined, {
              sensitivity: 'base',
            })
          )
        )
      )
      .catch((error) => dispatch(handleError({ error })))
      .finally(() => setLoadingPharmacies(false));
  }, [open, item, dispatch]);

  // Without this, the Select can't find a MenuItem matching an id whose
  // pharmacy hasn't loaded yet and silently renders blank -- indistinguishable
  // from "no pharmacy set". This says so instead, until the real name arrives.
  const renderPharmacyValue = (value) => {
    if (loadingPharmacies) return 'Loading…';
    if (!value) return <em>None</em>;
    return pharmacies.find((pharmacy) => pharmacy.compoundingPharmacyId === value)?.name ?? value;
  };

  const handleDialogClose = (_, reason) => {
    if (saving || reason === 'backdropClick') return;
    onClose();
  };

  const handleSave = () => {
    setSaving(true);

    updatePartnerCatalogApi({
      partnerUuid,
      partnerCatalog: [
        {
          catalogId: item.catalogId,
          isActive: item.isActive,
          compoundingPharmacyId: compoundingPharmacyId || null,
        },
      ],
    })
      .then(() => {
        enqueueSnackbar('Catalog Updated Successfully', { variant: 'success' });
        onClose();
        onSaved();
      })
      .catch((error) => dispatch(handleError({ error })))
      .finally(() => setSaving(false));
  };

  if (!item) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth='xs' fullWidth>
      <CatalogDialogHeader title={`Edit Catalog - ${item.drugName}`} onClose={handleDialogClose} />

      <DialogContent>
        <Grid container direction='column' rowSpacing={2} sx={{ mt: 1 }}>
          <Grid item>
            <Typography variant='body2' color='primary'>
              Drug Name
            </Typography>
            <Typography variant='body1'>{item.drugName}</Typography>
          </Grid>
          <Grid item>
            <Typography variant='body2' color='primary'>
              Strength
            </Typography>
            <Typography variant='body1'>{item.strength || 'N/A'}</Typography>
          </Grid>
          <Grid item>
            <Typography variant='body2' color='primary'>
              Pack Size / Form
            </Typography>
            <Typography variant='body1'>
              {formatPackSize(item.packSizeValue, item.packSizeUnit)} ·{' '}
              {formatForm(item.formAlias, item.dosageForm)}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant='body2' color='primary'>
              Is Available
            </Typography>
            <Typography variant='body1'>{item.isAvailable ? 'Yes' : 'No'}</Typography>
          </Grid>
        </Grid>

        <FormControl fullWidth variant='outlined' sx={{ mt: 3 }}>
          <InputLabel id='EditPartnerCatalogItemDialog-pharmacy-label' shrink>
            Compounding Pharmacy
          </InputLabel>
          <Select
            labelId='EditPartnerCatalogItemDialog-pharmacy-label'
            id='EditPartnerCatalogItemDialog-pharmacy-input'
            input={<OutlinedInput label='Compounding Pharmacy' />}
            value={compoundingPharmacyId}
            onChange={(event) => setCompoundingPharmacyId(event.target.value)}
            renderValue={renderPharmacyValue}
            displayEmpty
            notched
            MenuProps={{ sx: { maxHeight: 400 } }}
          >
            <MenuItem value=''>
              <em>None</em>
            </MenuItem>
            {pharmacies.map((pharmacy) => (
              <MenuItem key={pharmacy.compoundingPharmacyId} value={pharmacy.compoundingPharmacyId}>
                {pharmacy.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton loading={saving} variant='contained' onClick={handleSave} disabled={saving}>
          Update
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

EditPartnerCatalogItemDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  partnerUuid: PropTypes.string.isRequired,
  item: PropTypes.shape({
    catalogId: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    formAlias: PropTypes.string,
    dosageForm: PropTypes.string,
    packSizeValue: PropTypes.string,
    packSizeUnit: PropTypes.string,
    isActive: PropTypes.bool,
    isAvailable: PropTypes.bool,
    compoundingPharmacyId: PropTypes.string,
  }),
};

EditPartnerCatalogItemDialog.defaultProps = {
  item: null,
};

export default EditPartnerCatalogItemDialog;
